import { WithOwner, ownerData } from "../IDBSchema";
import { EntityDataApi } from "../IndexedDBApi";
import { getFromIdPayload, setDeletePayload } from "../../../schema/general";
import { CarryOutBase, CarryOutBasePatchPayload, CarryOutBasePutPayload } from "../../../schema/generated/CarryOut";
import { carryOutRow, carryOutRows } from "../../../schema/console/carry_out";
import { PutStatusResponse, StatusResponse } from "../../../schema/console/statusData";
import { MaterialBase } from "../../../schema/generated/Material";
import { LotBase } from "../../../schema/generated/Lot";
import { idbEntityDefs } from "../generated/entityDefs";
import { required } from "../../../util/typed";

export class CarryOutDataApi extends EntityDataApi<CarryOutBase> {
  public readonly def = idbEntityDefs.CarryOut;

  public async getAll(ownerId: string): Promise<CarryOutBase[]> {
    return await this.listByOwner(ownerId);
  }

  public async getById(payload: WithOwner<getFromIdPayload>): Promise<CarryOutBase | null> {
    return await this.findById(payload);
  }

  public async getRowById(payload: WithOwner<getFromIdPayload>): Promise<carryOutRow | null> {
    const carryOut = await this.getById(payload);
    if (!carryOut) {
      return null;
    }
    const [lots, allMaterials] = await Promise.all([
      this.IDB().lotDataApi.getByCarryOutId(carryOut.id, payload.ownerId),
      this.IDB().materialDataApi.getAll(payload.ownerId),
    ]);
    const lotsWithMaterials = CarryOutDataApi.attachMaterialsToLots(lots, allMaterials);
    const user = await this.IDB().userDataApi.getFullDataById({ id: carryOut.preparer_user_id, ownerId: payload.ownerId });
    return { ...carryOut, Lots: lotsWithMaterials, User: user };
  }

  public async getRowsById(payload: ownerData): Promise<carryOutRows> {
    const [carryOuts, allLots, allMaterials] = await Promise.all([
      this.getAll(payload.ownerId).then((arr) => arr.filter((c) => c.preparer_user_id === payload.ownerId)),
      this.IDB().lotDataApi.getAll(payload.ownerId),
      this.IDB().materialDataApi.getAll(payload.ownerId),
    ]);

    const lots = allLots.filter((lot) => carryOuts.some((co) => co.id === lot.carry_out_id));
    const lotsWithMaterials = CarryOutDataApi.attachMaterialsToLots(lots, allMaterials);

    const userIds = [...new Set(carryOuts.map((c) => c.preparer_user_id))];
    const users = await Promise.all(
      userIds.map((id) =>
        this.IDB().userDataApi.getFullDataById({ id, ownerId: payload.ownerId })
      )
    );
    const userMap = new Map(users.map((u) => [u.id, u]));
    return carryOuts.map((carryOut) => ({
      ...carryOut,
      Lots: lotsWithMaterials.filter((lot) => lot.carry_out_id === carryOut.id),
      User: required(userMap.get(carryOut.preparer_user_id), "登録者ユーザー"),
    }));
  }

  // lot_id => Material[] の索引を構築してLotへ紐付ける
  private static attachMaterialsToLots(lots: LotBase[], allMaterials: MaterialBase[]) {
    const lotIds = new Set(lots.map((l) => l.id));
    const materialsByLot = new Map<string, MaterialBase[]>();
    for (const m of allMaterials) {
      if (m.lot_id != null && lotIds.has(m.lot_id)) {
        const list = materialsByLot.get(m.lot_id);
        if (list) {
          list.push(m);
        } else {
          materialsByLot.set(m.lot_id, [m]);
        }
      }
    }
    return lots.map((lot) => ({
      ...lot,
      Materials: materialsByLot.get(lot.id) ?? [],
    }));
  }

  public async put(data: WithOwner<CarryOutBasePutPayload>): Promise<PutStatusResponse> {
    return await this.createRow(data);
  }

  public async patch(data: WithOwner<CarryOutBasePatchPayload>): Promise<StatusResponse> {
    return await this.patchRow(data);
  }

  public async delete(data: WithOwner<setDeletePayload>): Promise<StatusResponse> {
    return await this.softDelete(data);
  }
}
